import { ICellRendererParams, RowClassParams } from "ag-grid-community";

import { ContextMenu } from "../ContextMenu";
import { ContextMenuItems } from "../ContextMenu/types";

import { BaseTableData, TableColumn } from "./types";
import {
  COMPACT_DIMENSIONS,
  EXPANDED_DIMENSIONS,
  NORMAL_DIMENSIONS,
  TABLE_STYLE_VARS,
} from "./constants";

import { cn } from "@/lib/utils";

export const createCellRenderer =
  <T extends BaseTableData>(column: TableColumn<T>, contextMenuItems?: ContextMenuItems<T>[]) =>
  (params: ICellRendererParams<T>) => {
    // eslint-disable-next-line react/destructuring-assignment
    const cellContent = column.cellRenderer ? column.cellRenderer(params) : params.value;
    const contentClassName = column.cellContentClassName ?? "justify-center";
    const content = (
      <div
        className={cn("flex h-full w-full min-w-0 leading-none", contentClassName)}
        style={column.cellContentStyle}
      >
        {cellContent}
      </div>
    );

    // eslint-disable-next-line react/destructuring-assignment
    if (contextMenuItems && contextMenuItems.length > 0 && params.data) {
      return (
        <ContextMenu<T> // eslint-disable-next-line react/destructuring-assignment
          data={params.data}
          modal={false}
          className={cn(
            "flex h-full w-full min-w-0 items-center overflow-hidden px-3 leading-none",
            contentClassName,
          )}
          isDisabled={!contextMenuItems || contextMenuItems.length === 0}
          contextMenuItems={contextMenuItems}
        >
          {content}
        </ContextMenu>
      );
    }

    return content;
  };

export const getRowClasses = <T extends BaseTableData>({
  hoverable,
  onRowClick,
  onRowDoubleClick,
  striped,
  params,
  isHighlighted,
}: {
  hoverable: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowClick?: (row: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowDoubleClick?: (row: any) => void;
  striped: boolean;
  params: RowClassParams<T>;
  isHighlighted?: (row: T) => boolean;
}): string => {
  const classes: string[] = [];

  const shouldHighlight = isHighlighted && params.data && isHighlighted(params.data);

  if (shouldHighlight) {
    classes.push("bg-blue-50 hover:!bg-blue-100 [&.ag-full-width-row]:!bg-transparent");
  } else {
    if (hoverable) {
      classes.push("hover:bg-gray-50 [&.ag-full-width-row]:!bg-transparent");
    }

    if (striped && params.node?.rowIndex && params.node.rowIndex % 2 === 1) {
      classes.push("bg-gray-50/50");
    }
  }

  if (onRowClick || onRowDoubleClick) {
    classes.push("cursor-pointer");
  }

  return classes.join(" ");
};

export const createContainerStyle = ({
  hoverable,
  actualHeaderHeight,
}: {
  hoverable: boolean;
  actualHeaderHeight: number;
}): React.CSSProperties => {
  const dimensions = NORMAL_DIMENSIONS;

  return {
    "--ag-header-background-color": TABLE_STYLE_VARS.headerBackgroundColor,
    "--ag-header-foreground-color": TABLE_STYLE_VARS.headerForegroundColor,
    "--ag-row-hover-color": hoverable ? TABLE_STYLE_VARS.rowHoverColor : "transparent",
    "--ag-border-color": TABLE_STYLE_VARS.borderColor,
    "--ag-header-height": `${actualHeaderHeight}px`,
    "--ag-cell-horizontal-padding": TABLE_STYLE_VARS.cellHorizontalPadding,
    "--ag-font-size": dimensions.fontSize,
    "--ag-line-height": dimensions.lineHeight,
    "--ag-transition-duration": TABLE_STYLE_VARS.columnTransitionDuration,
  } as React.CSSProperties;
};

export const calculateActualDimensions = ({ compact }: { compact: boolean }) => ({
  actualRowHeight: compact ? COMPACT_DIMENSIONS.rowHeight : EXPANDED_DIMENSIONS.rowHeight,
  actualHeaderHeight: compact ? COMPACT_DIMENSIONS.headerHeight : EXPANDED_DIMENSIONS.headerHeight,
});
